(() => {
  'use strict';

  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const lockBody = () => document.body.classList.add('is-locked');
  const unlockBody = () => {
    if (!document.querySelector('.drawer-panel.is-open,.search-modal.is-open')) document.body.classList.remove('is-locked');
  };
  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };
  const formatMoney = (cents, format = window.theme?.moneyFormat || '€{{amount}}') => {
    const value = Number(cents || 0);
    const formatNumber = (precision = 2, thousands = ',', decimal = '.') => {
      const parts = (value / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(value / 100);
    let output = formatNumber();
    if (match[1] === 'amount_no_decimals') output = formatNumber(0);
    if (match[1] === 'amount_with_comma_separator') output = formatNumber(2, '.', ',');
    if (match[1] === 'amount_no_decimals_with_comma_separator') output = formatNumber(0, '.', ',');
    return format.replace(match[0], output);
  };
  window.NexaTheme = { announce, formatMoney, focusableSelector, lockBody, unlockBody };

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-minus],[data-quantity-plus]');
        if (!button || !this.input) return;
        event.preventDefault();
        const current = Number(this.input.value || 0);
        const step = Number(this.input.step || 1);
        const min = Number(this.input.min || 0);
        const next = button.hasAttribute('data-quantity-plus') ? current + step : Math.max(min, current - step);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  const createPanelController = ({ panel, openers, closers }) => {
    if (!panel) return null;
    let trigger = null;
    const open = (source) => {
      trigger = source || document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      openers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
      lockBody();
      window.setTimeout(() => panel.querySelector(focusableSelector)?.focus(), 50);
    };
    const close = () => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      openers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      unlockBody();
      trigger?.focus?.();
    };
    openers.forEach((item) => item.addEventListener('click', (event) => { event.preventDefault(); open(item); }));
    closers.forEach((item) => item.addEventListener('click', close));
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll(focusableSelector)].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    return { open, close };
  };

  const initHeader = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header || header.dataset.jsReady) return;
    header.dataset.jsReady = 'true';
    const update = () => {
      if (header.dataset.transparent === 'true') header.classList.toggle('is-scrolled', window.scrollY > 24);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    const menu = document.querySelector('[data-mobile-menu]');
    createPanelController({ panel: menu, openers: [...document.querySelectorAll('[data-menu-open]')], closers: [...(menu?.querySelectorAll('[data-menu-close]') || [])] });
  };

  const initSearch = () => {
    const modal = document.querySelector('[data-search-modal]');
    if (!modal || modal.dataset.jsReady) return;
    modal.dataset.jsReady = 'true';
    createPanelController({ panel: modal, openers: [...document.querySelectorAll('[data-search-open]')], closers: [...modal.querySelectorAll('[data-search-close]')] });
    document.querySelectorAll('[data-search-open]').forEach((button) => button.addEventListener('click', () => window.setTimeout(() => modal.querySelector('input[type="search"]')?.focus(), 100)));
    const input = modal.querySelector('[data-predictive-input]');
    const results = modal.querySelector('[data-predictive-results]');
    if (!input || !results) return;
    let timer;
    let aborter;
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      timer = window.setTimeout(async () => {
        aborter?.abort();
        aborter = new AbortController();
        const params = new URLSearchParams({ q: term, 'resources[type]': 'product,page,article', 'resources[limit]': '6', section_id: 'predictive-search' });
        try {
          const response = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: aborter.signal });
          if (!response.ok) throw new Error('Search failed');
          results.innerHTML = await response.text();
          input.setAttribute('aria-expanded', results.textContent.trim() ? 'true' : 'false');
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        }
      }, 220);
    });
  };

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.productInfo = this.closest('[data-product-info]');
      this.variants = JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
      this.addEventListener('change', () => this.updateVariant());
    }
    updateVariant() {
      const options = [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
      const variant = this.variants.find((item) => item.options.every((option, index) => option === options[index]));
      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const label = fieldset.querySelector('[data-selected-option]');
        const checked = fieldset.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      this.applyVariant(variant);
    }
    applyVariant(variant) {
      const scope = this.productInfo || document;
      scope.querySelectorAll('[data-variant-id]').forEach((input) => { input.value = variant?.id || ''; });
      document.querySelectorAll('[data-sticky-atc] [data-variant-id]').forEach((input) => { input.value = variant?.id || ''; });
      [...scope.querySelectorAll('[data-add-to-cart]'), ...document.querySelectorAll('[data-sticky-atc] [data-add-to-cart]')].forEach((button) => {
        button.disabled = !variant?.available;
        const label = button.querySelector('[data-add-to-cart-text]');
        if (label) label.textContent = !variant ? window.theme.strings.unavailable : variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
      });
      const price = scope.querySelector('[data-product-price]');
      if (price && variant) price.textContent = formatMoney(variant.price);
      const compare = scope.querySelector('[data-compare-price]');
      if (compare) {
        compare.textContent = variant?.compare_at_price > variant?.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant?.compare_at_price > variant?.price);
      }
      const stickyText = document.querySelector('[data-sticky-variant]');
      if (stickyText && variant) stickyText.textContent = `${variant.title !== 'Default Title' ? `${variant.title} · ` : ''}${formatMoney(variant.price)}`;
      if (variant && this.productInfo.dataset.url.startsWith('/')) history.replaceState({}, '', `${this.productInfo.dataset.url}?variant=${variant.id}`);
      if (variant?.featured_media?.id) this.selectMedia(variant.featured_media.id);
      announce(!variant ? window.theme.strings.unavailable : `${variant.title}. ${formatMoney(variant.price)}`);
    }
    selectMedia(id) {
      const gallery = document.querySelector('[data-product-gallery]');
      const target = gallery?.querySelector(`[data-media-id="${id}"]`);
      if (!target) return;
      gallery.querySelectorAll('[data-media-id]').forEach((item) => item.classList.toggle('is-active', item === target));
      gallery.querySelectorAll('[data-media-target]').forEach((item) => item.classList.toggle('is-active', item.dataset.mediaTarget === String(id)));
      if (window.innerWidth < 990) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  const initProductGallery = () => {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.jsReady) return;
      gallery.dataset.jsReady = 'true';
      gallery.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-media-target]');
        if (!thumb) return;
        const id = thumb.dataset.mediaTarget;
        gallery.querySelectorAll('[data-media-id]').forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === id));
        gallery.querySelectorAll('[data-media-target]').forEach((item) => item.classList.toggle('is-active', item === thumb));
      });
      const list = gallery.querySelector('[data-media-list]');
      const current = gallery.querySelector('[data-gallery-current]');
      if (list && current) list.addEventListener('scroll', () => window.requestAnimationFrame(() => {
        const items = [...list.children];
        const index = items.reduce((best, item, i) => Math.abs(item.offsetLeft - list.scrollLeft) < Math.abs(items[best].offsetLeft - list.scrollLeft) ? i : best, 0);
        current.textContent = index + 1;
      }), { passive: true });
    });
    const modal = document.querySelector('[data-media-modal]');
    if (!modal || modal.dataset.jsReady) return;
    modal.dataset.jsReady = 'true';
    const productSection = modal.closest('.main-product');
    productSection?.addEventListener('click', (event) => {
      const open = event.target.closest('[data-gallery-open]');
      if (open) {
        modal.querySelector('[data-modal-image]').src = open.dataset.fullSrc;
        modal.showModal();
        lockBody();
      }
      if (event.target.closest('[data-gallery-close]')) { modal.close(); unlockBody(); }
    });
    modal.addEventListener('click', (event) => { if (event.target === modal) { modal.close(); unlockBody(); } });
    modal.addEventListener('close', unlockBody);
  };

  const initStickyAtc = () => {
    const buttons = document.querySelector('[data-main-buy-buttons]');
    const sticky = document.querySelector('[data-sticky-atc]');
    if (!buttons || !sticky || sticky.dataset.jsReady || !('IntersectionObserver' in window)) return;
    sticky.dataset.jsReady = 'true';
    const observer = new IntersectionObserver(([entry]) => {
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
    observer.observe(buttons);
  };

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      const observer = new IntersectionObserver(async (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        try {
          const response = await fetch(this.dataset.url);
          const holder = document.createElement('div');
          holder.innerHTML = await response.text();
          const content = holder.querySelector('product-recommendations');
          if (content?.innerHTML.trim()) this.innerHTML = content.innerHTML;
        } catch (_) { /* Non-critical enhancement. */ }
      }, { rootMargin: '300px 0px' });
      observer.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  const initSizeGuide = () => {
    const modal = document.querySelector('[data-size-guide-modal]');
    if (!modal || modal.dataset.jsReady) return;
    modal.dataset.jsReady = 'true';
    const close = () => { if (modal.open) modal.close(); unlockBody(); };
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-guide-open]')) { event.preventDefault(); modal.showModal(); lockBody(); }
      if (event.target.closest('[data-size-guide-close]')) close();
    });
    modal.addEventListener('click', (event) => { if (event.target === modal) close(); });
    modal.addEventListener('close', unlockBody);
    modal.querySelectorAll('[data-size-unit]').forEach((button) => button.addEventListener('click', () => {
      const inches = button.dataset.sizeUnit === 'in';
      modal.querySelectorAll('[data-size-unit]').forEach((item) => item.classList.toggle('is-active', item === button));
      modal.querySelectorAll('[data-cm]').forEach((cell) => {
        const centimeters = Number(cell.dataset.cm);
        cell.textContent = inches ? (centimeters / 2.54).toFixed(1) : String(centimeters);
      });
    }));
  };

  const initWishlistHooks = () => {
    document.querySelectorAll('[data-wishlist-button]:not([data-wishlist-ready])').forEach((button) => {
      button.dataset.wishlistReady = 'true';
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        button.dispatchEvent(new CustomEvent('nexa:wishlist-request', { bubbles: true, detail: { productId: button.dataset.productId, handle: button.dataset.productHandle } }));
      });
    });
  };

  const initControls = () => {
    const filter = document.querySelector('[data-filter-drawer]');
    if (filter && !filter.dataset.jsReady) {
      filter.dataset.jsReady = 'true';
      createPanelController({ panel: filter, openers: [...document.querySelectorAll('[data-filter-open]')], closers: [...filter.querySelectorAll('[data-filter-close]')] });
    }
    document.querySelectorAll('[data-localization-select]:not([data-js-ready])').forEach((select) => { select.dataset.jsReady = 'true'; select.addEventListener('change', () => select.form.submit()); });
    document.querySelectorAll('[data-sort-select]:not([data-js-ready])').forEach((select) => { select.dataset.jsReady = 'true'; select.addEventListener('change', () => select.form.submit()); });
    document.querySelectorAll('[data-video-toggle]:not([data-js-ready])').forEach((button) => {
      button.dataset.jsReady = 'true';
      button.addEventListener('click', () => {
        const video = button.closest('section').querySelector('video');
        if (!video) return;
        if (video.paused) { video.play(); button.setAttribute('aria-label', 'Pause video'); }
        else { video.pause(); button.setAttribute('aria-label', 'Play video'); }
      });
    });
  };

  const initReveals = () => {
    const items = document.querySelectorAll('.reveal-on-scroll:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion:reduce)').matches) { items.forEach((item) => item.classList.add('is-visible')); return; }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    items.forEach((item) => observer.observe(item));
  };

  const init = () => {
    document.querySelectorAll('[data-variant-id]').forEach((input) => { input.name = 'id'; });
    initHeader();
    initSearch();
    initProductGallery();
    initStickyAtc();
    initSizeGuide();
    initWishlistHooks();
    initControls();
    initReveals();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
